import { Box, Center, CenterProps, Tooltip, useColorModeValue } from '@chakra-ui/react';
import {
  ArrowDownTrayIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUpOnSquareIcon,
  ExclamationCircleIcon,
  PaperClipIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Icon } from '@main/complykit/components/icon';
import { Spinner } from '@main/complykit/components/spinner';
import { Text } from '@main/complykit/components/text';
import { useStableCallback } from '@main/shared/browser';
import { Nullable } from '@main/shared/types';
import { identity } from '@main/shared/utils';
import { format as bytesFormat } from 'bytes';
import { ForwardedRef, forwardRef, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FileProcessingWarning } from '../file-processing-warning';
import { OverflowContainer } from '../overflow-tooltip';
import { Card, CardAction } from './base-card';

export interface UploadFile {
  name?: string | null;
  size?: number | null;
}

export interface FileCardPreview {
  readonly onPreview: () => void;
  readonly isPreviewDisabled?: boolean;
  readonly previewDisabledReason?: string;
}

export type UploadFileCardProps = {
  file: UploadFile;
  isLoading?: boolean;
  isUploaded?: boolean;
  isReadOnly?: boolean;
  error?: string;
  warning?: string;
  icon?: UploadFileCardIcon;
  actions?: UploadFileCardActionsFactory;
  onDelete?: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
  onReupload?: () => void;
  preview?: FileCardPreview;
};

export type UploadFileCardIcon = (icon: ReactNode, file: UploadFile) => ReactNode;

export type UploadFileCardActionsFactory = (
  actions: Nullable<CardAction>[],
  file: UploadFile,
) => Nullable<CardAction>[];

export const UploadFileCard = ({
  file,
  isLoading,
  isUploaded,
  isReadOnly,
  error,
  warning,
  icon = identity,
  actions = identity,
  onDownload,
  isDownloading,
  onDelete,
  onReupload,
  preview,
}: UploadFileCardProps) => {
  const { t } = useTranslation('ui');
  const bgColor = useColorModeValue('blue.50', 'rgba(214, 188, 250, 0.16)');
  const errorBgColor = useColorModeValue('red.100', 'rgba(254, 178, 178, 0.16)');
  const iconColor = useColorModeValue('purple.800', 'purple.200');

  const getFileStateIcon = useStableCallback(() => {
    if (isLoading) {
      return <Spinner size="sm" />;
    }
    if (isUploaded) {
      return <Icon as={PaperClipIcon} />;
    }
    if (error) {
      return <Icon as={XCircleIcon} color="error" />;
    }

    return <Icon as={ArrowUpOnSquareIcon} />;
  });

  return (
    <Card
      data-testid="file-stack"
      aria-busy={isLoading}
      isReadOnly={isReadOnly}
      onPreview={
        isUploaded && !isLoading && !error && !preview?.isPreviewDisabled
          ? preview?.onPreview
          : undefined
      }
      previewLabel={file.name ?? undefined}
      title={preview?.isPreviewDisabled ? preview.previewDisabledReason : undefined}
    >
      <Card.Header
        left={icon(
          /* The chip owns the brand tint; the icon inside reads it through
           * currentColor. The error glyph names its own status role instead. */
          <UploadFileCardIcon bgColor={error ? errorBgColor : bgColor} color={iconColor}>
            {getFileStateIcon()}
          </UploadFileCardIcon>,
          file,
        )}
        right={
          <Text size="xs" color="muted" isNoWrap>
            {bytesFormat(file.size || 0, { unitSeparator: ' ' })}
          </Text>
        }
        actions={actions(
          [
            onDownload && !isLoading
              ? {
                  icon: ArrowDownTrayIcon,
                  label: t('cards.actions.download'),
                  onClick: onDownload,
                  isLoading: isDownloading,
                  isRead: true,
                }
              : undefined,
            onReupload && {
              icon: ArrowPathRoundedSquareIcon,
              label: t('cards.actions.reupload'),
              onClick: onReupload,
            },
            onDelete && {
              icon: TrashIcon,
              label: t('cards.actions.delete'),
              onClick: onDelete,
            },
          ],
          file,
        )}
      />
      <Card.Body gap={warning ? 3 : 2} ml={warning ? -1 : undefined}>
        {warning && <FileProcessingWarning label={warning} variant="attachment-card" />}

        <OverflowContainer w="fit-content">
          <Box maxW={['2xs', 'xs']}>
            <OverflowContainer.Tooltip
              label={file.name}
              hasArrow
              placement="top"
              gutter={12}
              fontSize="sm"
              openDelay={500}
            >
              <Text size="sm" weight="medium" isTruncated>
                {file.name}
              </Text>
            </OverflowContainer.Tooltip>
          </Box>
        </OverflowContainer>

        {error && (
          /* shouldWrapChildren because the tooltip attaches its hover handlers
           * by cloning props onto its child, and Icon exposes a closed prop
           * surface rather than a spread. */
          <Tooltip label={error} placement="top" hasArrow shouldWrapChildren>
            <Icon as={ExclamationCircleIcon} color="error" />
          </Tooltip>
        )}
      </Card.Body>
    </Card>
  );
};

const UploadFileCardIcon = forwardRef(
  (
    { bgColor, children, ...props }: PropsWithChildren<{ bgColor?: string } & CenterProps>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <Center {...props} bg={bgColor} p={1.5} rounded="md" ref={ref}>
        {children}
      </Center>
    );
  },
);

UploadFileCard.Icon = UploadFileCardIcon;
